import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import staffIdService from '../../../services/staffIdService';

const StaffIdApprovalModal = ({ cardId, onClose, onApprovalComplete }) => {
  const [approvalData, setApprovalData] = useState(null);
  const [approvalSteps, setApprovalSteps] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    loadApprovalData();
  }, [cardId]);

  const loadApprovalData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [statusData, stepsData, historyData] = await Promise.all([
        staffIdService?.getStaffIdApprovalStatus(cardId),
        staffIdService?.getStaffIdApprovalSteps(statusData?.approvalInstanceId),
        staffIdService?.getStaffIdApprovalHistory(statusData?.approvalInstanceId)
      ]);

      setApprovalData(statusData);
      setApprovalSteps(stepsData || []);
      setApprovalHistory(historyData || []);
    } catch (err) {
      setError(err?.message || 'Failed to load approval data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <Icon name="CheckCircle" size={20} className="text-success" />;
      case 'rejected':
        return <Icon name="XCircle" size={20} className="text-error" />;
      case 'pending':
        return <Icon name="Clock" size={20} className="text-warning" />;
      default:
        return <Icon name="AlertCircle" size={20} className="text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success/10 text-success border-success';
      case 'rejected':
        return 'bg-error/10 text-error border-error';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card rounded-lg p-8 max-w-4xl w-full mx-4">
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" className="animate-spin h-8 w-8 text-primary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Approval Details</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Card Number: {approvalData?.cardNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4 flex items-start gap-2">
            <Icon name="AlertCircle" size={18} className="mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Employee Information */}
        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3">Employee Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Name</p>
              <p className="text-sm font-medium text-foreground">
                {approvalData?.employee?.firstName} {approvalData?.employee?.lastName}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Employee Number</p>
              <p className="text-sm font-medium text-foreground">{approvalData?.employee?.employeeNumber}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Department</p>
              <p className="text-sm font-medium text-foreground">{approvalData?.employee?.department}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Position</p>
              <p className="text-sm font-medium text-foreground">{approvalData?.employee?.position}</p>
            </div>
          </div>
        </div>

        {/* Overall Status */}
        <div className="mb-6">
          <div className={`flex items-center gap-3 p-4 rounded-lg border ${getStatusColor(approvalData?.approvalStatus)}`}>
            {getStatusIcon(approvalData?.approvalStatus)}
            <div className="flex-1">
              <p className="text-sm font-semibold">Overall Status</p>
              <p className="text-xs opacity-80">
                {approvalData?.approvalStatus?.replace('_', ' ')?.toUpperCase()}
              </p>
            </div>
            {approvalData?.approvalStatus === 'rejected' && approvalData?.rejectionReason && (
              <div className="text-right">
                <p className="text-xs font-medium">Rejection Reason:</p>
                <p className="text-xs opacity-80">{approvalData?.rejectionReason}</p>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-4 border-b-2 transition-colors ${
                activeTab === 'details' ?'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Approval Steps
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-2 px-4 border-b-2 transition-colors ${
                activeTab === 'history' ?'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              History
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <div className="space-y-4">
            {approvalSteps?.map((step, index) => (
              <div
                key={step?.id}
                className={`border rounded-lg p-4 ${
                  step?.stepStatus === 'approved' ?'border-success bg-success/5'
                    : step?.stepStatus === 'rejected' ?'border-error bg-error/5'
                    : step?.stepStatus === 'pending' ?'border-warning bg-warning/5' :'border-border'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step?.stepStatus === 'approved' ?'bg-success text-white'
                        : step?.stepStatus === 'rejected' ?'bg-error text-white'
                        : step?.stepStatus === 'pending' ?'bg-warning text-white' :'bg-muted text-muted-foreground'
                    }`}>
                      {step?.stepStatus === 'approved' ? (
                        <Icon name="Check" size={20} />
                      ) : step?.stepStatus === 'rejected' ? (
                        <Icon name="X" size={20} />
                      ) : (
                        <span className="text-sm font-bold">{step?.stepOrder}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-foreground">{step?.stepName}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        step?.stepStatus === 'approved' ?'bg-success/20 text-success'
                          : step?.stepStatus === 'rejected' ?'bg-error/20 text-error'
                          : step?.stepStatus === 'pending' ?'bg-warning/20 text-warning' :'bg-muted text-muted-foreground'
                      }`}>
                        {step?.stepStatus?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Assigned to: {step?.assignedToRole}
                      {step?.assignedToEmail && ` (${step?.assignedToEmail})`}
                    </p>
                    {step?.approvedBy && (
                      <p className="text-xs text-muted-foreground mb-1">
                        {step?.stepStatus === 'approved' ? 'Approved' : 'Rejected'} by: {step?.approvedBy}
                      </p>
                    )}
                    {step?.approvedAt && (
                      <p className="text-xs text-muted-foreground mb-1">
                        Date: {new Date(step?.approvedAt)?.toLocaleString()}
                      </p>
                    )}
                    {step?.comments && (
                      <div className="mt-2 p-2 bg-muted/50 rounded text-xs text-foreground">
                        <p className="font-medium mb-1">Comments:</p>
                        <p>{step?.comments}</p>
                      </div>
                    )}
                    {step?.rejectionReason && (
                      <div className="mt-2 p-2 bg-error/10 rounded text-xs text-error">
                        <p className="font-medium mb-1">Rejection Reason:</p>
                        <p>{step?.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-3">
            {approvalHistory?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No history available</p>
            ) : (
              approvalHistory?.map((entry) => (
                <div key={entry?.id} className="border border-border rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <Icon name="Activity" size={16} className="text-primary mt-1" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{entry?.actionDescription}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        By: {entry?.performedBy} • {new Date(entry?.createdAt)?.toLocaleString()}
                      </p>
                      {entry?.metadata && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {entry?.metadata?.comments && <p>Comments: {entry?.metadata?.comments}</p>}
                          {entry?.metadata?.rejection_reason && (
                            <p className="text-error">Reason: {entry?.metadata?.rejection_reason}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-3">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
          {approvalData?.approvalStatus === 'rejected' && (
            <Button
              onClick={() => {
                onApprovalComplete?.();
                onClose();
              }}
              className="bg-warning hover:bg-warning/90"
            >
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Resubmit for Approval
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffIdApprovalModal;
